import React, { useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  House, Warehouse, Package, Storefront, ShoppingCart, Invoice, ChartLine,
  ClipboardText, Barcode, SignOut, Users, ArrowsLeftRight, ShieldCheck,
  UserList, Calculator, Buildings, Money, ArrowUUpLeft, HourglassMedium,
} from "@phosphor-icons/react";

// Role-based access map — determines sidebar visibility and route guarding.
const ACCESS = {
  super_admin: {
    warehouse: true, stores_manage: true, store_ops: true, invoicing: true,
    inwarding: true, reco: true, reports: true, users: true, audit: true,
    customers: true, day_close: true, settings: true, cash: true,
  },
  warehouse_manager: {
    warehouse: true, stores_manage: false, store_ops: false, invoicing: false,
    inwarding: false, reco: false, reports: false, users: false, audit: false,
    customers: true, day_close: false, settings: false,
  },
  store_manager: {
    warehouse: false, stores_manage: false, store_ops: true, invoicing: true,
    inwarding: true, reco: true, reports: true, users: false, audit: false,
    customers: true, day_close: true, settings: false, cash: true,
  },
  cashier: {
    warehouse: false, stores_manage: false, store_ops: true, invoicing: true,
    inwarding: false, reco: false, reports: false, users: false, audit: false,
    customers: true, day_close: false, settings: false, cash: true,
  },
  operator: {
    warehouse: false, stores_manage: false, store_ops: true, invoicing: false,
    inwarding: true, reco: true, reports: true, users: false, audit: false,
    customers: false, day_close: false, settings: false,
  },
};

// Per-permission grants — admins can attach ANY of these via Users → Permissions
// to expand any role beyond its base ACCESS map (eg an operator with `pos.bill`).
const PERMISSION_GRANTS = {
  "*": { warehouse: true, stores_manage: true, store_ops: true, invoicing: true,
         inwarding: true, reco: true, reports: true, users: true, audit: true,
         customers: true, day_close: true, settings: true },
  "pos.bill": { store_ops: true, invoicing: true, customers: true },
  "pos.refund": { store_ops: true, invoicing: true },
  "invoices.view": { store_ops: true, invoicing: true },
  "invoices.edit": { store_ops: true, invoicing: true },
  "invoices.cancel": { store_ops: true, invoicing: true },
  "inventory.view": { store_ops: true },
  "inventory.inward": { store_ops: true, inwarding: true },
  "inventory.reco": { store_ops: true, reco: true },
  "inventory.bulk_import": { store_ops: true },
  "inventory.edit": { store_ops: true },
  "barcodes.print": { store_ops: true },
  "warehouse.view": { warehouse: true },
  "warehouse.sku.edit": { warehouse: true },
  "warehouse.dispatch": { warehouse: true },
  "reports.view": { store_ops: true, reports: true },
  "reports.export": { store_ops: true, reports: true },
  "day_close.manage": { store_ops: true, day_close: true },
  "customers.view": { customers: true },
  "stores.manage": { stores_manage: true },
  "users.manage": { users: true },
  "audit.view": { audit: true },
  "settings.manage": { settings: true },
};

export function can(roleOrUser, key) {
  // Backward-compatible: accept either a role string OR the full user object.
  const role = typeof roleOrUser === "string" ? roleOrUser : roleOrUser?.role;
  if (role === "super_admin") return true;
  // Preferred: effective permissions computed by the backend
  // (role defaults + custom grants − custom revokes).
  const eff = typeof roleOrUser === "object" && roleOrUser ? roleOrUser.effective_permissions : null;
  if (Array.isArray(eff)) {
    if (eff.includes("*")) return true;
    return eff.some((p) => PERMISSION_GRANTS[p] && PERMISSION_GRANTS[p][key]);
  }
  // Fallback (user object not loaded yet): role defaults + raw grants
  const perms = typeof roleOrUser === "object" && roleOrUser ? (roleOrUser.permissions || []) : [];
  if (ACCESS[role] && ACCESS[role][key]) return true;
  for (const p of perms) {
    if (PERMISSION_GRANTS[p] && PERMISSION_GRANTS[p][key]) return true;
  }
  return false;
}

export function hasPerm(user, perm) {
  // Check one SPECIFIC permission (used for fine-grained items like POS billing).
  if (!user) return false;
  if (user.role === "super_admin") return true;
  const eff = user.effective_permissions;
  if (Array.isArray(eff)) return eff.includes("*") || eff.includes(perm);
  return (user.permissions || []).includes("*") || (user.permissions || []).includes(perm);
}

const Item = ({ to, icon: Icon, label, testId, end = false }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors border-l-[3px] ${
        isActive
          ? "bg-white/5 text-emerald-400 border-emerald-500"
          : "text-slate-300 border-transparent hover:bg-white/5 hover:text-white"
      }`
    }
  >
    <Icon size={18} weight="duotone" />
    <span>{label}</span>
  </NavLink>
);

const Section = ({ title, children }) => (
  <div className="mt-5">
    <div className="px-4 mb-1 text-[10px] uppercase tracking-widest font-semibold text-slate-500">{title}</div>
    <div>{children}</div>
  </div>
);

export default function Sidebar() {
  const { user, stores, currentStoreId, switchStore, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const role = user?.role || "cashier";
  // Wrap user so the per-key checks use BOTH role and custom permissions.
  const principal = user || { role };
  const currentStore = stores.find((s) => s.id === currentStoreId);

  // Switching store must also move the CURRENT page to the new store — otherwise
  // a page like /store/<old-id>/invoices keeps showing (and exporting) the old store.
  const onSwitchStore = (id) => {
    switchStore(id);
    const m = location.pathname.match(/^\/store\/([^/]+)(\/.*)?$/);
    if (m && m[1] !== id) navigate(`/store/${id}${m[2] || ""}`, { replace: true });
  };

  // Keep the dropdown in sync when a store page is opened directly by URL / bookmark.
  const urlStoreId = (location.pathname.match(/^\/store\/([^/]+)/) || [])[1];
  useEffect(() => {
    if (urlStoreId && urlStoreId !== currentStoreId && stores.some((s) => s.id === urlStoreId)) switchStore(urlStoreId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlStoreId, stores]);

  return (
    <aside className="bg-slate-900 text-white w-[250px] flex-shrink-0 flex flex-col h-screen sticky top-0 border-r border-slate-800 scrollbar-thin overflow-y-auto" data-testid="app-sidebar">
      <div className="px-5 py-5 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-emerald-500 flex items-center justify-center">
            <ShoppingCart size={18} weight="bold" className="text-slate-900" />
          </div>
          <div>
            <div className="font-display font-bold text-[15px] leading-none">SND · POS</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Retail Ops</div>
          </div>
        </div>
      </div>

      {can(principal, "store_ops") && stores.length > 0 && (
        <div className="px-4 py-3 border-b border-slate-800">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Active Store</div>
          <select
            data-testid="store-switcher"
            value={currentStoreId || ""}
            onChange={(e) => onSwitchStore(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-md px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {stores.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          {currentStore && (
            <div className="mt-1.5 text-[10px] text-slate-500 font-mono-num">{currentStore.code}</div>
          )}
        </div>
      )}

      <nav className="flex-1 py-2">
        <Item to="/" icon={House} label="Overview" testId="nav-overview" end />

        {can(principal, "warehouse") && (
          <Section title="Main Warehouse">
            <Item to="/warehouse/skus" icon={Package} label="SKU Master" testId="nav-skus" />
            <Item to="/warehouse/dispatches" icon={Warehouse} label="Delivery Challans" testId="nav-dispatches" />
            {!can(principal, "store_ops") && can(principal, "customers") && <Item to="/customers" icon={UserList} label="Customers" testId="nav-customers-wh" />}
          </Section>
        )}

        {can(principal, "store_ops") && (
          <Section title="Store Operations">
            {can(principal, "stores_manage") && <Item to="/stores" icon={Storefront} label="Manage Stores" testId="nav-manage-stores" />}
            {currentStoreId && <>
              <Item to={`/store/${currentStoreId}`} icon={ChartLine} label="Store Dashboard" testId="nav-store-dash" end />
              {hasPerm(principal, "pos.bill") && <Item to={`/store/${currentStoreId}/pos`} icon={ShoppingCart} label="POS Billing" testId="nav-pos" />}
              <Item to={`/store/${currentStoreId}/inventory`} icon={Package} label="Inventory" testId="nav-inventory" />
              {can(principal, "inwarding") && <Item to={`/store/${currentStoreId}/inwarding`} icon={ArrowsLeftRight} label="Inwarding" testId="nav-inwarding" />}
              <Item to={`/store/${currentStoreId}/barcodes`} icon={Barcode} label="Barcodes" testId="nav-barcodes" />
              {can(principal, "invoicing") && <Item to={`/store/${currentStoreId}/invoices`} icon={Invoice} label="Invoices" testId="nav-invoices" />}
              {can(principal, "invoicing") && <Item to={`/store/${currentStoreId}/credit-notes`} icon={ArrowUUpLeft} label="Credit Notes" testId="nav-credit-notes" />}
              {can(principal, "invoicing") && <Item to={`/store/${currentStoreId}/pending-payments`} icon={HourglassMedium} label="Pending Payments" testId="nav-pending" />}
              {can(principal, "reco") && <Item to={`/store/${currentStoreId}/reco`} icon={ClipboardText} label="Reconciliation" testId="nav-reco" />}
              {can(principal, "reports") && <Item to={`/store/${currentStoreId}/reports`} icon={ChartLine} label="Reports" testId="nav-reports" />}
              {can(principal, "day_close") && <Item to={`/store/${currentStoreId}/day-close`} icon={Calculator} label="Day Close · Z-Report" testId="nav-day-close" />}
              {can(principal, "cash") && <Item to={`/store/${currentStoreId}/cash`} icon={Money} label="Cash Register" testId="nav-cash" />}
            </>}
            {can(principal, "customers") && <Item to="/customers" icon={UserList} label="Customers" testId="nav-customers" />}
          </Section>
        )}

        {(can(principal, "users") || can(principal, "audit") || can(principal, "stores_manage") || can(principal, "settings")) && (
          <Section title="Admin">
            {can(principal, "stores_manage") && !can(principal, "store_ops") && <Item to="/stores" icon={Storefront} label="Manage Stores" testId="nav-manage-stores-top" />}
            {can(principal, "users") && <Item to="/users" icon={Users} label="Users" testId="nav-users" />}
            {can(principal, "audit") && <Item to="/audit" icon={ShieldCheck} label="Audit Log" testId="nav-audit" />}
            {can(principal, "settings") && <Item to="/settings/company" icon={Buildings} label="Company Settings" testId="nav-company-settings" />}
          </Section>
        )}
      </nav>

      <div className="border-t border-slate-800 p-4">
        <div className="flex items-center justify-between">
          <div className="text-xs">
            <div className="font-medium text-white truncate max-w-[150px]" data-testid="current-user-name">{user?.name || "User"}</div>
            <div className="text-slate-500 text-[10px] uppercase tracking-widest">{(user?.role || "").replace("_", " ")}</div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="p-2 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
            title="Logout"
          >
            <SignOut size={16} weight="bold" />
          </button>
        </div>
      </div>
    </aside>
  );
}
